/**
 * Additional registrations on an extension (owner, 18 September 2026): up to two more SIP logins,
 * each named by whoever adds it -- luke-meridian, sarah-meridian -- each with its own password and
 * its own switch for whether inbound calls ring it. Unique on the account: adding a name that is
 * already there is refused, never merged.
 *
 * The username is the name plus the account (the first label of its SIP domain), exactly as the
 * main registration is the number plus the account (1001-meridian, docs/26 E8).
 */
import { z } from 'zod';
import { IdSchema } from './common.js';

export const ADDITIONAL_REGISTRATIONS_MAX = 2;

/**
 * Lower-case letters, digits, dot and underscore, starting with a letter; 2 to 30 characters.
 * No hyphen: it separates the name from the account, and the edge reads it that way. Starting with
 * a letter keeps a name from ever looking like an extension's own login (1001-meridian).
 */
export const REGISTRATION_NAME_RE = /^[a-z][a-z0-9._]{1,29}$/;

export const RegistrationCreateSchema = z.object({
  name: z.string().trim().toLowerCase().regex(REGISTRATION_NAME_RE,
    'Use 2 to 30 letters, digits, dots or underscores, starting with a letter'),
}).strict();

export const RegistrationUpdateSchema = z.object({ ring_enabled: z.boolean() }).strict();

export const AdditionalRegistrationSchema = z.object({
  id: IdSchema,
  name: z.string(),
  username: z.string(),
  ring_enabled: z.boolean(),
  created_at: z.string(),
});
export type AdditionalRegistration = z.infer<typeof AdditionalRegistrationSchema>;

export interface ExtensionRegistrations {
  /** The extension's own login, e.g. 1001-meridian. */
  main: string | null;
  /** The account part every name gets, e.g. "meridian". */
  account: string;
  additional: AdditionalRegistration[];
  max: number;
}

/** The username a name becomes on an account. */
export const registrationUsername = (name: string, account: string): string => `${name}-${account}`;
